import { useState, useEffect } from "react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Globe, Shield, FileCheck, Users, Building2, CheckCircle, ArrowRight, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";

// Modern luxury skyscrapers
const DIASPORA_HERO_BG = "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=2000&q=80";
// Luxury mansion aerial view
const DIASPORA_WHY_BG = "https://images.unsplash.com/photo-1635111031688-9b13c0125d12?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjByZWFsJTIwZXN0YXRlJTIwbWFuc2lvbiUyMGFlcmlhbCUyMHZpZXd8ZW58MHx8fHwxNzczMDcyODU1fDA&ixlib=rb-4.1.0&q=85";

const whatWeHelp = [
  {
    icon: FileCheck,
    title: "Property Due Diligence",
    description: "Comprehensive verification of property documents, ownership history, and legal status before you invest."
  },
  {
    icon: Shield,
    title: "Title Verification & Perfection",
    description: "Ensure your property has clear, unencumbered title and all necessary government consents."
  },
  {
    icon: Building2,
    title: "Remote Property Acquisition",
    description: "We represent you in all negotiations, inspections, and transactions without requiring your physical presence."
  },
  {
    icon: Users,
    title: "Family Property Matters",
    description: "Navigate complex family land disputes, inheritance issues, and succession planning with expert guidance."
  }
];

const whyDiaspora = [
  "You cannot physically inspect properties before purchase",
  "Nigerian property law has unique complexities foreigners may not understand",
  "High risk of title fraud and land disputes without proper verification",
  "Family members may not have legal expertise to protect your interests",
  "Distance makes it difficult to attend to urgent property matters",
  "You need someone trustworthy to handle sensitive transactions"
];

export default function DiasporaPage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <main className="min-h-screen bg-[#FAFAF9]" data-testid="diaspora-page">
      <Navigation scrolled={scrolled} />
      
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center pt-20">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${DIASPORA_HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0B]/95 via-[#0A0A0B]/85 to-[#0A0A0B]/70" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <Globe className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-[#D4AF37] text-sm tracking-[0.25em] uppercase font-medium">
                Diaspora Services
              </span>
            </div>
            
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-6" data-testid="diaspora-hero-title">
              Property Legal Advisory for
              <span className="block text-[#D4AF37] mt-2">Diaspora Investors</span>
            </h1>
            
            <p className="text-lg text-white/80 leading-relaxed mb-10 max-w-2xl">
              Investing in Nigerian real estate from abroad? We provide end-to-end legal protection 
              for your property investments—so you can build wealth back home with confidence, 
              even when you can't be there in person.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/#booking"
                className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A0A0B] px-8 py-4 text-sm tracking-[0.1em] font-bold uppercase hover:bg-[#C5A028] transition-all"
                data-testid="diaspora-cta-book"
              >
                Book Diaspora Consultation
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="https://wa.me/2348032143826?text=Hello%204ACES%20Attorneys%2C%20I%20saw%20your%20website%20and%20would%20like%20to%20discuss%20a%20property%20transaction%20in%20Nigeria."
                className="inline-flex items-center justify-center gap-2 border border-white/30 text-white px-8 py-4 text-sm tracking-[0.1em] font-medium uppercase hover:bg-white/10 transition-all"
                data-testid="diaspora-cta-whatsapp"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What We Help With */}
      <section className="py-24 lg:py-32 bg-white" data-testid="diaspora-services">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#D4AF37] text-sm tracking-[0.25em] uppercase font-medium">
              Comprehensive Support
            </span>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl text-[#1A1A1A] mt-4 mb-6">
              What We Help Diaspora Investors With
            </h2>
            <p className="text-[#1A1A1A]/60 text-lg">
              From initial property search to final documentation, we handle every legal aspect 
              of your Nigerian property investment.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {whatWeHelp.map((item, index) => (
              <div
                key={index}
                className="p-8 bg-[#FAFAF9] border border-[#E5E5E5] hover:border-[#D4AF37]/30 transition-colors group"
                data-testid={`diaspora-service-${index}`}
              >
                <div className="w-14 h-14 bg-[#D4AF37]/10 flex items-center justify-center mb-6 group-hover:bg-[#D4AF37]/20 transition-colors">
                  <item.icon className="w-7 h-7 text-[#D4AF37]" />
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-3">
                  {item.title}
                </h3>
                <p className="text-[#1A1A1A]/60 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Diaspora Investors Need This */}
      <section className="py-24 lg:py-32 relative overflow-hidden" data-testid="diaspora-why">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${DIASPORA_WHY_BG})` }}
        />
        <div className="absolute inset-0 bg-[#0A0A0B]/90" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#D4AF37] text-sm tracking-[0.25em] uppercase font-medium">
                The Challenge
              </span>
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl text-white mt-4 mb-6">
                Why Diaspora Investors Need Expert Legal Support
              </h2>
              <p className="text-white/70 text-lg leading-relaxed mb-8">
                Investing in Nigerian property from abroad comes with unique challenges. 
                Without proper legal representation, your investment is at risk.
              </p>
              
              <Link
                to="/#booking"
                className="inline-flex items-center gap-2 text-[#D4AF37] font-medium hover:gap-3 transition-all"
              >
                Protect Your Investment
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="space-y-4">
              {whyDiaspora.map((reason, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 p-4 bg-white/5 border border-white/10"
                >
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <p className="text-white/80">{reason}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 bg-[#D4AF37]" data-testid="diaspora-final-cta">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl text-[#0A0A0B] mb-6">
            Ready to Invest with Confidence?
          </h2>
          <p className="text-[#0A0A0B]/70 text-lg mb-10 max-w-2xl mx-auto">
            Schedule a consultation with our diaspora services team and get expert legal 
            guidance for your Nigerian property investment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/#booking"
              className="inline-flex items-center justify-center gap-2 bg-[#0A0A0B] text-white px-10 py-4 text-sm tracking-[0.1em] font-bold uppercase hover:bg-[#1A1A1A] transition-all"
              data-testid="diaspora-book-final"
            >
              Book Diaspora Consultation
            </Link>
            <a
              href="https://wa.me/2348032143826?text=Hello%204ACES%20Attorneys%2C%20I%20saw%20your%20website%20and%20would%20like%20to%20discuss%20a%20property%20transaction%20in%20Nigeria."
              className="inline-flex items-center justify-center gap-2 border-2 border-[#0A0A0B] text-[#0A0A0B] px-10 py-4 text-sm tracking-[0.1em] font-bold uppercase hover:bg-[#0A0A0B] hover:text-white transition-all"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
